import { CircleAlert, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '../../../../lib/utils'
import { ExpandableErrorMessage, type StatusTone } from './ExpandableErrorMessage'

const TONE_ICON: Record<StatusTone, LucideIcon> = {
  error: CircleAlert,
  warning: TriangleAlert,
  info: Info,
}

const TONE_ICON_CLASS: Record<StatusTone, string> = {
  error: 'text-destructive',
  warning: 'text-warning',
  info: 'text-info',
}

const INLINE_TONE_CLASS: Record<StatusTone, string> = {
  error: 'border-destructive/30 bg-destructive/5',
  warning: 'border-warning/30 bg-warning/5',
  info: 'border-info/30 bg-info/5',
}

/**
 * The translucent strip over the bottom of a node's preview. Shared with
 * `NodeDownloadPreparingBanner` so the download banner sits in exactly the
 * same slot.
 */
export const NODE_STATUS_OVERLAY_CLASS =
  'pointer-events-none absolute inset-x-0 bottom-0 z-20 border-t border-white/10 bg-black/65 px-2.5 py-1.5 backdrop-blur-[2px] transition-[bottom] duration-150'

type NodeStatusMessageProps = {
  message: string | undefined
  tone?: StatusTone
  /**
   * `overlay` (default) floats a translucent strip over the bottom of the
   * node's preview area, so a failure never changes the card's outline size and
   * never hides the whole preview. The parent must be `position: relative`.
   * The strip itself is pointer-transparent — only the message is clickable —
   * so whatever the preview does on click (open config, retry upload) still
   * works through its padding.
   * `inline` is for panels, where the message sits in normal flow.
   */
  placement?: 'overlay' | 'inline'
  title?: string
  testId?: string
  className?: string
  /**
   * Renders a close control. The caller owns the dismissed state, so it decides
   * what "dismissed" survives (e.g. until the message text changes).
   */
  onDismiss?: () => void
}

/**
 * One status treatment for every node (Workplace post 1634117708345526: the
 * error text should be "smaller and less alarming").
 *
 * Severity is carried by a coloured icon, not by the text: the message is
 * neutral `text-xs`, clamped to two lines, and clicking it opens the full text
 * in the fixed-width popup owned by `ExpandableErrorMessage`.
 */
export function NodeStatusMessage({
  message,
  tone = 'error',
  placement = 'overlay',
  title,
  testId,
  className,
  onDismiss,
}: NodeStatusMessageProps) {
  const { t } = useTranslation('canvas')
  if (!message) return null
  const Icon = TONE_ICON[tone]
  return (
    <div
      className={cn(
        'flex items-start gap-1.5',
        placement === 'overlay'
          ? NODE_STATUS_OVERLAY_CLASS
          : cn('rounded border px-2 py-1.5', INLINE_TONE_CLASS[tone]),
        className,
      )}
      data-testid={testId ? `${testId}-status` : undefined}
      data-status-tone={tone}
      data-status-placement={placement}
    >
      <Icon className={cn('mt-0.5 h-3 w-3 shrink-0', TONE_ICON_CLASS[tone])} strokeWidth={2} aria-hidden="true" />
      <ExpandableErrorMessage
        message={message}
        title={title}
        tone={tone}
        testId={testId}
        className="pointer-events-auto min-w-0 text-xs text-foreground/85"
      />
      {onDismiss && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss()
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="pointer-events-auto -my-0.5 -mr-1 ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded text-foreground/60 transition-colors hover:bg-white/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary nodrag"
          aria-label={t('nodeStatusMessage.dismiss')}
          title={t('nodeStatusMessage.dismiss')}
          data-testid={testId ? `${testId}-dismiss` : undefined}
        >
          <X className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
